import { useState, useRef } from 'react';
import { Upload, Loader2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const CHUNK_SIZE = 4 * 1024 * 1024;

export default function BackupImportCard({ onRestored }) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('merge');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const runImport = async () => {
    setConfirmOpen(false);
    setResult(null);
    const uploadId = crypto.randomUUID();
    const total = Math.ceil(file.size / CHUNK_SIZE);
    try {
      for (let i = 0; i < total; i++) {
        const form = new FormData();
        form.append('chunk', file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        await api.post(`/admin/backup/import/chunk?upload_id=${uploadId}&index=${i}`, form, {
          headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0,
        });
        setProgress({ step: 'upload', pct: Math.round(((i + 1) / total) * 90) });
      }
      setProgress({ step: 'restore', pct: 95 });
      const res = await api.post('/admin/backup/import/complete', { upload_id: uploadId, total_chunks: total, mode }, { timeout: 0 });
      setProgress({ step: 'done', pct: 100 });
      setResult(res.data);
      toast.success('Backup ripristinato con successo');
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      onRestored?.();
    } catch (e) {
      setProgress(null);
      toast.error(e.response?.data?.detail || 'Errore durante il ripristino');
    }
  };

  const busy = progress && progress.step !== 'done';

  return (
    <Card data-testid="backup-import-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Upload size={18} /> Ripristina da backup</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">Seleziona un file di backup <code>.json</code> scaricato da questa pagina.</p>
        <input
          ref={inputRef}
          type="file"
          accept=".json,application/json"
          disabled={busy}
          onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setProgress(null); }}
          className="block w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-foreground file:text-background file:text-sm"
          data-testid="backup-file-input"
        />
        <RadioGroup value={mode} onValueChange={setMode} className="space-y-2" data-testid="backup-mode-group">
          <div className="flex items-start gap-2">
            <RadioGroupItem value="merge" id="mode-merge" data-testid="backup-mode-merge" />
            <Label htmlFor="mode-merge" className="text-sm leading-snug">
              <span className="font-medium">Unisci (consigliato)</span> — aggiunge gli elementi mancanti e aggiorna quelli esistenti, senza cancellare nulla
            </Label>
          </div>
          <div className="flex items-start gap-2">
            <RadioGroupItem value="replace" id="mode-replace" data-testid="backup-mode-replace" />
            <Label htmlFor="mode-replace" className="text-sm leading-snug">
              <span className="font-medium">Sostituisci</span> — cancella i contenuti attuali e li rimpiazza con quelli del backup
            </Label>
          </div>
        </RadioGroup>
        {progress && (
          <div className="space-y-1">
            <Progress value={progress.pct} className="h-2" />
            <p className="text-xs text-muted-foreground" data-testid="backup-import-progress">
              {progress.step === 'upload' && `Caricamento file... ${progress.pct}%`}
              {progress.step === 'restore' && 'Ripristino contenuti in corso...'}
              {progress.step === 'done' && 'Completato'}
            </p>
          </div>
        )}
        {result && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm" data-testid="backup-import-result">
            <p className="font-medium text-emerald-800 mb-1">Ripristinato (backup del {new Date(result.exported_at).toLocaleString('it-IT')})</p>
            <p className="text-emerald-700">
              {Object.entries(result.restored).filter(([, n]) => n > 0).map(([k, n]) => `${k}: ${n}`).join(' · ')}
            </p>
          </div>
        )}
        <Button onClick={() => setConfirmOpen(true)} disabled={!file || busy} className="w-full" variant={mode === 'replace' ? 'destructive' : 'default'} data-testid="backup-import-btn">
          {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
          {busy ? 'Ripristino...' : 'Ripristina backup'}
        </Button>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent data-testid="backup-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="text-amber-600" size={20} /> Confermi il ripristino?</AlertDialogTitle>
              <AlertDialogDescription>
                File: <strong>{file?.name}</strong> ({file ? (file.size / 1024 / 1024).toFixed(1) : 0} MB).{' '}
                {mode === 'replace'
                  ? 'Modalità Sostituisci: tutti i contenuti attuali (progetti, foto, servizi, prenotazioni...) verranno cancellati e sostituiti con quelli del backup. L\'operazione non è reversibile.'
                  : 'Modalità Unisci: gli elementi del backup verranno aggiunti o aggiornati, nulla verrà cancellato.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="backup-confirm-cancel">Annulla</AlertDialogCancel>
              <AlertDialogAction onClick={runImport} data-testid="backup-confirm-ok">Sì, ripristina</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
